import * as React from 'react';
import styled from 'styled-components';
import { ResponsiveValue, style } from 'styled-system';
import Link from 'next/link';

import {
  Text,
  themeGet,
  media,
  Button,
  Anchor,
  InputPopover,
  PopoverPosition,
  InputPopoverStyled,
  InputAnimatedStyled,
  InputAnimated,
} from '@packages/ui';
import { NewsletterHeadingForContentful } from './NewsletterHeadingForContentful';
import { NewsletterFooterSubmitMessage } from './NewsletterFooterSubmitMessage';

const Container = styled.form<{ leftPopoverVisible: ResponsiveValue<boolean> }>`
  ${InputAnimatedStyled} {
    width: 288px;
    ${media.to.sm`
      margin-bottom: ${themeGet.space(2)};
    `}
  }

  ${InputPopoverStyled} {
    font-size: ${themeGet.fontSize(6)};
    ${style({
      prop: 'leftPopoverVisible',
      cssProperty: 'display',
      transformValue: (show) => (show ? 'block' : 'none'),
    })}
  }

  ${InputPopoverStyled} ~ ${InputPopoverStyled} {
    ${style({
      prop: 'leftPopoverVisible',
      cssProperty: 'display',
      transformValue: (show) => (show ? 'none' : 'block'),
    })}
  }
`;

const TextContainer = styled.div`
  margin-right: ${themeGet.space(2)};
  h3 {
    padding: 0;
    margin: 0;
  }
  a {
    color: ${themeGet.colors('primary')};
    text-decoration: underline;
  }

  ${media.to.lg`
    text-align: center;
    margin-bottom: 12px;

    h3 {
      margin-bottom:${themeGet.space(2)};
    }
  `}
`;

const ContentHolder = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  padding-bottom: ${themeGet.space(3)};
`;

const FormElements = styled.div`
  display: flex;
  ${media.to.sm`
      flex-wrap: wrap;
  `}
  ${media.to.lg`
      justify-content: center;
  `}
    &
    > * {
    margin: 0 ${themeGet.space(2)};
    align-self: center;
  }
`;

export interface NewsletterFooterInputSectionProps {
  mainText: string;
  dataProtectionNote: string;
  dataProtectionLinkUrl: string;
  dataProtectionLinkLabel: string;
  signUpButtonLabel: string;
  signUpButtonPlaceholder: string;
  onSubmit: (email: string) => void;
  disableSubmitButton?: boolean;
  errorMessage?: string;
  invalidEmailErrorMessage: string;
  showContentfulHeading: boolean;
  trendsMsg: string;
  hightlightsMsg: string;
  offersMsg: string;
  isSubmitted: boolean;
  submitMessage: string;
}

export const NewsletterFooterInputSection: React.FC<NewsletterFooterInputSectionProps> = ({
  mainText,
  dataProtectionNote,
  dataProtectionLinkUrl,
  dataProtectionLinkLabel,
  signUpButtonLabel,
  signUpButtonPlaceholder,
  disableSubmitButton = false,
  onSubmit: submit,
  errorMessage: errorMsg,
  invalidEmailErrorMessage,
  showContentfulHeading,
  hightlightsMsg,
  offersMsg,
  trendsMsg,
  isSubmitted,
  submitMessage,
}) => {
  const [errorMessage, setErrorMessage] = React.useState<string>();
  const [email, setEmail] = React.useState<string>();
  const [inputTarget, setInputTarget] = React.useState<EventTarget & HTMLInputElement>();

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const target = e?.target;
    setInputTarget(target);

    const email = target?.value;
    setEmail(email);
  };

  React.useEffect(() => {
    setErrorMessage(errorMsg);
  }, [errorMsg]);

  React.useEffect(() => {
    if (inputTarget) {
      const msg = errorMessage ? ' ' : '';
      inputTarget.setCustomValidity(msg);
    }
  }, [errorMessage, inputTarget]);

  const validationCallback = (e: React.FocusEvent<HTMLInputElement>) => {
    const emailIsValid = e.target.checkValidity();
    if (emailIsValid || !email) {
      setErrorMessage(undefined);
    } else if (!errorMessage) {
      setErrorMessage(invalidEmailErrorMessage);
    }
  };

  const onSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    submit(email);
  };

  return (
    <Container onSubmit={onSubmit} leftPopoverVisible={[false, false, false, false, true]} noValidate={true}>
      <ContentHolder className="newsletterContentHolder">
        <TextContainer className="textContainer">
          <Text tag="h3" fontWeight="semibold" fontSize={6}>
            {!showContentfulHeading && !isSubmitted && mainText}
            {showContentfulHeading && <NewsletterHeadingForContentful msgs={[trendsMsg, hightlightsMsg, offersMsg]} />}
          </Text>
          {!isSubmitted && (
            <Text tag="span" fontSize={4}>
              {dataProtectionNote}{' '}
              <Link prefetch={false} href={dataProtectionLinkUrl}>
                <Anchor href={dataProtectionLinkUrl} target="_blank">
                  {dataProtectionLinkLabel}
                </Anchor>
              </Link>
            </Text>
          )}
        </TextContainer>
        {isSubmitted ? (
          <NewsletterFooterSubmitMessage submitMessage={submitMessage} />
        ) : (
          <FormElements className="newsletterFormElements">
            <InputAnimated
              placeholder={signUpButtonPlaceholder}
              type="email"
              onChange={handleChange}
              onBlur={validationCallback}
            >
              {errorMessage && (
                <>
                  <InputPopover position={PopoverPosition.Left}>{errorMessage}</InputPopover>
                  <InputPopover position={PopoverPosition.Top}>{errorMessage}</InputPopover>
                </>
              )}
            </InputAnimated>
            <Button type="submit" disabled={disableSubmitButton}>
              {signUpButtonLabel}
            </Button>
          </FormElements>
        )}
      </ContentHolder>
    </Container>
  );
};
